/**
 * DSCR tier margins — `/correspondents/admin/tier_margins`.
 *
 * One row per tier (Gold / Silver / Bronze / Base); `_id` is the tier name.
 * `marginPoints` is required on upsert (the backend 400s without it) and every
 * value on the row is BOND POINTS or a dollar loan bound — never a rate.
 *
 * The upsert is a FULL config write, so the editor dialog posts every field it
 * shows, seeded from the current row.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddRounded from "@mui/icons-material/AddRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import RefreshRounded from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import { adminTierMarginsApi } from "../../../api/adminPricingConfigApi";
import { ActionCell, actionColumn } from "../shared/adminTableActions";
import {
  lastChangeColumn,
  newestHistoryEdit,
  PRICING_GRID_COL_DEF,
  PricingField,
  PricingGrid,
  PricingSection,
  SectionEmpty,
} from "./pricingUi";
import VersionHistoryDialog from "./VersionHistoryDialog";

// The editable config fields the backend accepts (`_TIER_CONFIG_FIELDS`).
const FIELDS = [
  {
    key: "marginPoints",
    label: "Margin (bond points)",
    required: true,
    numeric: true,
  },
  { key: "label", label: "Label" },
  { key: "maxPrice", label: "Max price", numeric: true },
  { key: "maxPriceShortPPP", label: "Max price (short PPP)", numeric: true },
  { key: "minPrice", label: "Min price", numeric: true },
  { key: "minLoanAmount", label: "Min loan amount", numeric: true },
  { key: "maxLoanAmount", label: "Max loan amount", numeric: true },
  { key: "description", label: "Description", full: true },
  { key: "notes", label: "Notes", full: true, multiline: true },
];

const tierOf = (row) => row?._id || row?.tier || "";

export default function TierMarginsPanel() {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { tier, form } | null
  const [historyTier, setHistoryTier] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminTierMarginsApi
      .list()
      .then((r) => setRows(r))
      .catch((err) => {
        setRows([]);
        enqueueSnackbar(
          err?.response?.data?.detail || "Couldn't load tier margins",
          { variant: "error" },
        );
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = useCallback((row) => {
    setEditing({
      tier: tierOf(row),
      isNew: !row,
      form: FIELDS.reduce(
        (acc, f) => ({ ...acc, [f.key]: row?.[f.key] ?? "" }),
        {},
      ),
    });
  }, []);

  const save = useCallback(() => {
    if (!editing?.tier?.trim()) return;
    const body = {};
    FIELDS.forEach((f) => {
      const raw = editing.form[f.key];
      if (raw === "" || raw == null) return;
      body[f.key] = f.numeric ? Number(raw) : raw;
    });
    if (body.marginPoints == null || Number.isNaN(body.marginPoints)) {
      enqueueSnackbar("Margin (bond points) is required", {
        variant: "warning",
      });
      return;
    }
    adminTierMarginsApi
      .upsert(editing.tier.trim(), body)
      .then(() => {
        enqueueSnackbar(`${editing.tier} saved`, { variant: "success" });
        setEditing(null);
        load();
      })
      .catch((err) =>
        enqueueSnackbar(err?.response?.data?.detail || "Save failed", {
          variant: "error",
        }),
      );
  }, [editing, enqueueSnackbar, load]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Tier",
        colId: "tier",
        valueGetter: (p) => tierOf(p.data),
        width: 140,
        cellClass: "key",
        sort: "asc",
      },
      { headerName: "Label", field: "label", minWidth: 160, flex: 1 },
      {
        headerName: "Margin (pts)",
        field: "marginPoints",
        width: 140,
        cellClass: "num",
        headerClass: "num",
        valueFormatter: (p) =>
          p.value == null ? "—" : Number(p.value).toFixed(3),
      },
      {
        headerName: "Min price",
        field: "minPrice",
        width: 120,
        cellClass: "num",
        headerClass: "num",
        valueFormatter: (p) => (p.value == null ? "—" : String(p.value)),
      },
      {
        headerName: "Max price",
        field: "maxPrice",
        width: 120,
        cellClass: "num",
        headerClass: "num",
        valueFormatter: (p) => (p.value == null ? "—" : String(p.value)),
      },
      {
        headerName: "Max price (short PPP)",
        field: "maxPriceShortPPP",
        width: 185,
        cellClass: "num",
        headerClass: "num",
        valueFormatter: (p) => (p.value == null ? "—" : String(p.value)),
      },
      {
        headerName: "Loan range",
        colId: "loanRange",
        minWidth: 200,
        cellClass: "num",
        headerClass: "num",
        valueGetter: (p) =>
          p.data?.minLoanAmount == null && p.data?.maxLoanAmount == null
            ? "—"
            : `${formatDollar(p.data?.minLoanAmount, "—")} – ${formatDollar(p.data?.maxLoanAmount, "—")}`,
      },
      lastChangeColumn(),
      actionColumn({
        count: 2,
        cellRenderer: (p) => (
          <ActionCell>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEdit(p.data)}>
                <EditOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Version history">
              <IconButton
                size="small"
                onClick={() => setHistoryTier(tierOf(p.data))}
              >
                <HistoryOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </ActionCell>
        ),
      }),
    ],
    [openEdit],
  );

  return (
    <>
      <PricingSection
        id="cfg-tier-margins"
        title={`DSCR tier margins${rows ? ` (${rows.length})` : ""}`}
        lastEdit={newestHistoryEdit(rows, tierOf)}
        desc="Bond points (par = 100) per correspondent tier, plus the price ceilings and loan bounds that go with them. This list IS the tier dropdown — nothing is hardcoded."
        actions={
          <Flx ac g={1}>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={load} disabled={loading} size="small">
                  <RefreshRounded fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => openEdit(null)}
            >
              Add tier
            </Button>
          </Flx>
        }
        dense
      >
        {rows && rows.length === 0 ? (
          <SectionEmpty>
            No tiers configured. Until a tier exists, correspondents on that
            tier resolve to no margin.
          </SectionEmpty>
        ) : (
          <PricingGrid rowCount={(rows || []).length} maxRows={10}>
            <AgGridReact
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={PRICING_GRID_COL_DEF}
              getRowId={(p) => String(tierOf(p.data))}
              tooltipShowDelay={300}
              loading={loading}
            />
          </PricingGrid>
        )}
      </PricingSection>

      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          {editing?.isNew ? "Add tier" : `Edit ${editing?.tier}`}
        </DialogTitle>
        {/* Labels sit ABOVE their control (the app's form convention — see
            CLAUDE.md): the field never gets a MUI `label`, so no notch. */}
        <DialogContent dividers>
          <Flx column g={2.5} sx={{ pt: 1 }}>
            <PricingField
              fullWidth
              label="Tier"
              required
              helperText={
                editing?.isNew
                  ? "The tier key — this is what the correspondent tier dropdown shows."
                  : "The tier key is the document id and can't be renamed."
              }
            >
              <TextField
                size="small"
                value={editing?.tier || ""}
                disabled={!editing?.isNew}
                onChange={(e) =>
                  setEditing((prev) => ({ ...prev, tier: e.target.value }))
                }
                fullWidth
              />
            </PricingField>
            <Flx g={2} wrap sx={{ alignItems: "flex-end" }}>
              {FIELDS.map((f) => (
                <PricingField
                  key={f.key}
                  label={f.label}
                  required={f.required}
                  sx={
                    f.full ? { flex: "1 1 100%" } : { flex: 1, minWidth: 200 }
                  }
                >
                  <TextField
                    size="small"
                    multiline={f.multiline}
                    minRows={f.multiline ? 2 : undefined}
                    value={editing?.form?.[f.key] ?? ""}
                    onChange={(e) =>
                      setEditing((prev) => ({
                        ...prev,
                        form: { ...prev.form, [f.key]: e.target.value },
                      }))
                    }
                    fullWidth
                  />
                </PricingField>
              ))}
            </Flx>
          </Flx>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <VersionHistoryDialog
        open={!!historyTier}
        title={`Tier margin history — ${historyTier}`}
        onClose={() => setHistoryTier(null)}
        fetcher={() => adminTierMarginsApi.history(historyTier)}
      />
    </>
  );
}
